import { faCheck, faCircleSmall, IconDefinition } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import { cva } from 'class-variance-authority';
import * as React from 'react';

import { Avatar } from '../Avatar';
import { BrandBadge } from '../BrandBadge';
import { MenuType, MenuTypeEnum, Theme, ThemeEnum } from '../types';
import { cn } from '../utils/cn';

export const iconSubtleStyle = cva('text-sm', {
  variants: {
    theme: {
      dark: '!text-slate-500 group-data-[disabled]:text-slate-600 group-data-[highlighted]:text-slate-500',
      light: '!text-slate-400',
    },
  },
  defaultVariants: {
    theme: ThemeEnum.Dark,
  },
});

export const iconStyles = cva('text-sm', {
  variants: {
    theme: {
      dark: 'text-slate-100 group-data-[disabled]:text-slate-600',
      light: 'text-slate-900 group-data-[disabled]:text-slate-400',
    },
  },
  defaultVariants: {
    theme: ThemeEnum.Dark,
  },
});

export const menuItemStyles = cva(
  'dropdown-menu-item group relative flex flex-col cursor-pointer select-none h-8 px-3 py-1 justify-center text-sm outline-none transition-colors data-[disabled]:pointer-events-none data-[disabled]:text-slate-400 w-full',
  {
    variants: {
      theme: {
        dark: 'bg-slate-800 text-slate-100 focus:bg-slate-500 focus:bg-opacity-25',
        light: 'bg-white text-slate-900 focus:bg-slate-100',
      },
    },
    defaultVariants: {
      theme: ThemeEnum.Dark,
    },
  },
);

const DropdownMenuShortcut = ({ className, ...props }: React.HTMLAttributes<HTMLSpanElement>) => {
  return (
    <span className={cn('text-xs font-normal leading-none text-slate-400', className)} {...props} />
  );
};

const iconContainerStyles = cva('dropdown-menu-item-icon flex items-center justify-center', {
  variants: {
    menuType: {
      item: 'size-4',
      checkbox: 'size-5 pl-2 mr-1',
      radio: 'size-4 mr-2',
    },
  },
  defaultVariants: {
    menuType: MenuTypeEnum.Item,
  },
});

export interface DropdownMenuItemBaseProps {
  showAvatar?: boolean;
  avatarProps?: React.ComponentProps<typeof Avatar>;
  shortcutText?: string;
  showShortcut?: boolean;
  showTag?: boolean;
  subtext?: string;
  subtextProps?: React.ComponentProps<'div'> & { className?: string };
  tagComponent?: React.ReactNode;
  icon?: IconDefinition;
  iconClassName?: string;
  theme?: Theme;
  children?: React.ReactNode;
  childrenClassName?: string;
  menuType?: MenuType;
}

const DropdownMenuItemBase = ({
  avatarProps,
  children,
  childrenClassName,
  icon,
  iconClassName,
  menuType,
  shortcutText,
  showAvatar,
  showShortcut,
  showTag,
  tagComponent,
  theme,
}: DropdownMenuItemBaseProps) => {
  const tag = tagComponent || <BrandBadge type='pro' theme={theme} />;

  return (
    <>
      {showAvatar && (
        <div className='pl-1'>
          <Avatar size='xs' {...avatarProps} />
        </div>
      )}
      {icon && (
        <span className={iconContainerStyles({ menuType })}>
          <FontAwesomeIcon
            icon={icon}
            className={cn(iconSubtleStyle({ theme }), 'text-sm transition-colors', iconClassName)}
          />
        </span>
      )}
      <span className={cn('px-1 text-left truncate', childrenClassName)}>{children}</span>
      <div className='flex flex-grow justify-end'>
        {showTag && tag}
        {showShortcut && shortcutText && (
          <DropdownMenuShortcut>{shortcutText}</DropdownMenuShortcut>
        )}
      </div>
    </>
  );
};

/**
 * The component that contains the dropdown menu items.
 * @see {@link https://stylus.design/dropdown}
 */
export const DropdownMenuItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Item> & DropdownMenuItemBaseProps
>(
  (
    {
      avatarProps,
      children,
      childrenClassName,
      className,
      icon,
      iconClassName,
      shortcutText,
      showAvatar,
      showShortcut,
      showTag,
      subtext,
      theme,
      ...props
    },
    ref,
  ) => (
    <DropdownMenuPrimitive.Item
      ref={ref}
      className={cn(menuItemStyles({ theme }), { 'h-auto': !!subtext }, className)}
      {...props}
    >
      <div className='flex gap-1 items-center w-full'>
        <DropdownMenuItemBase
          showAvatar={showAvatar}
          avatarProps={avatarProps}
          icon={icon}
          iconClassName={iconClassName}
          theme={theme}
          showTag={showTag}
          showShortcut={showShortcut}
          shortcutText={shortcutText}
          subtext={subtext}
          childrenClassName={childrenClassName}
        >
          {children}
        </DropdownMenuItemBase>
      </div>

      {subtext && (
        <div className={cn('px-1 text-xs text-slate-400', { 'ml-5': !!icon })}>{subtext}</div>
      )}
    </DropdownMenuPrimitive.Item>
  ),
);

/**
 * An item that can be controlled and rendered like a checkbox.
 * @see {@link https://stylus.design/components/dropdown-menu#checkboxes}
 */
export const DropdownMenuCheckboxItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.CheckboxItem>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.CheckboxItem> &
    DropdownMenuItemBaseProps
>(
  (
    {
      avatarProps,
      checked,
      children,
      childrenClassName,
      className,
      icon,
      iconClassName,
      showAvatar,
      showTag,
      subtext,
      tagComponent,
      theme,
      ...props
    },
    ref,
  ) => (
    <DropdownMenuPrimitive.CheckboxItem
      ref={ref}
      className={cn(menuItemStyles({ theme }), { 'h-auto': !!subtext }, className)}
      checked={checked}
      {...props}
    >
      <div className='flex gap-1 items-center w-full'>
        <div className='flex-none size-5'>
          <DropdownMenuPrimitive.ItemIndicator className='flex justify-center items-center size-5'>
            <FontAwesomeIcon icon={faCheck} className={iconStyles({ theme })} />
          </DropdownMenuPrimitive.ItemIndicator>
        </div>
        <DropdownMenuItemBase
          showAvatar={showAvatar}
          avatarProps={avatarProps}
          icon={icon}
          iconClassName={iconClassName}
          theme={theme}
          childrenClassName={childrenClassName}
          menuType={MenuTypeEnum.Checkbox}
          showTag={showTag}
          tagComponent={tagComponent}
        >
          {children}
        </DropdownMenuItemBase>
      </div>

      {subtext && (
        <div className={cn('px-1 ml-6 text-xs mt-[2px] text-slate-400', { 'ml-5': !!icon })}>
          {subtext}
        </div>
      )}
    </DropdownMenuPrimitive.CheckboxItem>
  ),
);

/**
 * An item that can be controlled and rendered like a radio.
 * @see {@link https://stylus.design/components/dropdown-menu#radio-group}
 */
export const DropdownMenuRadioItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.RadioItem>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.RadioItem> & DropdownMenuItemBaseProps
>(
  (
    {
      avatarProps,
      children,
      childrenClassName,
      className,
      icon,
      showAvatar,
      subtext,
      subtextProps = {},
      theme,
      ...props
    },
    ref,
  ) => {
    const { className: subtextClassName, ...restSubtextProps } = subtextProps;

    return (
      <DropdownMenuPrimitive.RadioItem
        ref={ref}
        className={cn(menuItemStyles({ theme }), { 'h-auto': !!subtext }, className)}
        {...props}
      >
        <div className='grid grid-cols-[1.75rem_min-content_1fr] grid-rows-[auto_auto] gap-y-0.5 items-center'>
          <div className='w-5 h-5'>
            <DropdownMenuPrimitive.ItemIndicator className='flex justify-center items-center w-5 h-5 size-5'>
              <FontAwesomeIcon icon={faCircleSmall} className={iconStyles({ theme })} />
            </DropdownMenuPrimitive.ItemIndicator>
          </div>
          {icon ? (
            <div className={iconContainerStyles({ menuType: 'radio' })}>
              <FontAwesomeIcon
                icon={icon}
                className={cn(iconSubtleStyle({ theme }), 'text-sm transition-colors size-4')}
              />
            </div>
          ) : showAvatar ? (
            <div className='mr-2'>
              <Avatar size='xs' {...avatarProps} />
            </div>
          ) : (
            <span />
          )}
          <div>{children}</div>
          <div />
          <div />
          <div className={cn('text-xs text-slate-400', subtextClassName)} {...restSubtextProps}>
            {subtext}
          </div>
        </div>
      </DropdownMenuPrimitive.RadioItem>
    );
  },
);
